"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";

import ContentLayout from "@/components/layouts/ContentLayout";
import AppText from "@/components/other/AppText";

import Image from "next/image";
import CustomLink from "@/components/other/CustomLink";
import { AppIconButton } from "../buttons/AppIconButton";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TITLE_SMALL } from "@/styles";
import { cn } from "@/lib/utils";
import { NO_PREVIEW_PLACEHOLDER } from "@/contentful/constants";
import { useMediaQuery } from "@/hooks/mediaQuery";

interface ICard {
  id: string;
  heading: string;
  subheading: string;
  ctaLink: string;
  ctaLabel: string;
  imgSrc: string;
  placeholder: string;
  imgAlt: string;
}
interface ProductCategoryOurSolutionsSectionProps {
  label: string;
  heading: string;
  subheading: string;
  internalLabel: string;
  cards: ICard[];
  el: any;
}

const ProductCategoryOurSolutionsSection: React.FC<ProductCategoryOurSolutionsSectionProps> = ({
  label,
  heading,
  subheading,
  internalLabel,
  cards,
}) => {
  const isHomePageProducts = internalLabel
    .replace(/\s+/g, "")
    .toLowerCase()
    .includes("homepage-products".replace(/\s+/g, "").toLowerCase());

  return (
    <>
      <ContentLayout
        bgColorClass={`${isHomePageProducts ? "white" : "bg-black-5"}`}
        innerClass={`${isHomePageProducts ? "py-9 sm:py-14" : ""}`}
      >
        <div className="">
          <div className={`flex flex-col ${isHomePageProducts ? "sm:mb-0 lg:mb-4" : "mb-6"}`}>
            {label && (
              <AppText
                type={"LABEL_LARGE"}
                className={`text-royal-blue mb-2 ${isHomePageProducts ? "text-center" : ""}`}
              >
                {label}
              </AppText>
            )}
            {heading && (
              <AppText type="HEADLINE_SMALL" className={`mb-4 ${isHomePageProducts ? "text-center" : ""}`}>
                {heading}
              </AppText>
            )}
            {subheading && (
              <AppText type={"BODY_SMALL"} className={`mb-4 ${isHomePageProducts ? "text-center" : ""}`}>
                {subheading}
              </AppText>
            )}
          </div>

          {isHomePageProducts ? (
            <MobileCardCarousel cards={cards} />
          ) : (
            <div className={"grid grid-cols-8 gap-6"}>
              {(cards || []).map((card) => (
                <SolutionCard {...card} key={card.id} />
              ))}
            </div>
          )}
        </div>
      </ContentLayout>
    </>
  );
};

export default React.memo(ProductCategoryOurSolutionsSection);

interface ISolutionCard {
  ctaLabel: string;
  ctaLink: string;
  heading?: string;
  id: string;
  imgSrc: string;
  placeholder: string;
  imgAlt: string;
  subheading: string;
  paddingBottom?: string;
  isHomePageProducts?: boolean;
}
const SolutionCard = ({
  ctaLabel,
  ctaLink,
  id,
  imgSrc,
  imgAlt,
  subheading,
  placeholder,
  paddingBottom,
}: ISolutionCard) => {
  return (
    <CustomLink
      href={ctaLink}
      key={`card-product-${id}`}
      className={cn(
        "bg-white group flex flex-col sm:col-span-4 col-span-8 lg:col-span-2 gap-2 p-4 rounded-[3px]",
        paddingBottom
      )}
    >
      <div className="relative overflow-hidden aspect-[16/9]">
        <Image
          src={imgSrc}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 320px"
          fill
          placeholder="blur"
          blurDataURL={placeholder || NO_PREVIEW_PLACEHOLDER}
          className="group-hover:scale-105 object-contain w-full transition"
          alt={imgAlt}
        />
      </div>

      <AppIconButton
        intent="ghost"
        iconOnRightSide
        icon={<ChevronRight size={20} aria-label="chevron right icon" />}
        label={ctaLabel}
        className={cn(TITLE_SMALL, "whitespace-normal text-start")}
      />
      <AppText type={"BODY_MEDIUM"}>{subheading}</AppText>
    </CustomLink>
  );
};

const ProductsSolutionCard = ({
  ctaLink,
  id,
  imgSrc,
  imgAlt,
  subheading,
  placeholder,
  paddingBottom,
  heading,
}: ISolutionCard) => {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  return (
    <CustomLink
      href={ctaLink}
      key={`card-product-${id}`}
      className={cn("bg-white group flex flex-col gap-2 text-center pb-2 rounded-[3px]", paddingBottom)}
    >
      <div className="relative overflow-hidden aspect-[16/9]">
        <Image
          src={imgSrc}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 320px"
          fill
          placeholder="blur"
          blurDataURL={placeholder || NO_PREVIEW_PLACEHOLDER}
          className="group-hover:scale-105 object-contain w-full transition"
          alt={imgAlt}
        />
      </div>
      <div className="font-sairaSemiCondensed text-[20px] font-[600]">
        {heading?.toLocaleLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())}
      </div>
      {!isDesktop && (
        <AppText type={"BODY_MEDIUM"} className="line-clamp-3">
          {subheading}
        </AppText>
      )}
    </CustomLink>
  );
};

interface MobileCardCarouselProps {
  cards: ISolutionCard[];
}
const MobileCardCarousel: React.FC<MobileCardCarouselProps> = ({ cards }) => {
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEndX(null);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    const distance = touchStartX - touchEndX;

    if (distance > minSwipeDistance && currentIndex < cards.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else if (distance < -minSwipeDistance && currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNext = () => {
    if (currentIndex < cards.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  if (cards.length === 0) return null;

  return (
    <div className="w-full">
      <div className="relative flex items-center justify-center min-h-[333px]">
        {!isDesktop && cards.length > 1 && (
          <button
            disabled={currentIndex === 0}
            onClick={handlePrev}
            aria-label="Next card"
            className="left-2 top-1/2 disabled:opacity-30 absolute z-10 -translate-y-1/2 p-2 border border-[#666] rounded-md bg-transparent"
          >
            <ChevronLeft size={18} />
          </button>
        )}

        {isDesktop ? (
          <div className="sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 grid w-full grid-cols-1 gap-6">
            {cards.map((card: any) => (
              <ProductsSolutionCard key={card.id} {...card} />
            ))}
          </div>
        ) : (
          <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
            <ProductsSolutionCard {...cards[currentIndex]} key={cards[currentIndex].id} />
          </div>
        )}

        {!isDesktop && cards.length > 1 && (
          <button
            disabled={currentIndex === cards.length - 1}
            onClick={handleNext}
            aria-label="previous card"
            className="right-2 top-1/2 disabled:opacity-30 absolute z-10 -translate-y-1/2 p-2 border border-[#666] rounded-md bg-transparent"
          >
            <ChevronRight size={18} />
          </button>
        )}
      </div>

      {!isDesktop && cards.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {Array.from({ length: Math.min(cards.length, 4) }).map((_, i) => {
            const allFilled = currentIndex >= 4;
            const isActive = allFilled || i <= currentIndex;
            return <span key={i} className={`h-2 w-2 rounded-full ${isActive ? "bg-black" : "bg-gray-300"}`} />;
          })}
        </div>
      )}
    </div>
  );
};
